// Roadmap tab — the customer's Flightpath (Phase 4). Renders GET /roadmap (adapter
// over today's Jira, best-effort with ~derived markers). ?mock=1 keeps the design
// mock. docs/design/2026-07-17-tickets-intelligence.md §Roadmap + design "Roadmap".
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import { getRoadmap, type RoadmapView, type RoadmapRequest, type RoadmapLane, type RoadmapStage } from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { KpiCard, MonoLabel, AgentText, Sparkle } from "../../mui";
import { useAgent } from "../../agent/AgentProvider";
import { mockRoadmapView } from "./mocks/roadmapView.mock";

const c = brainTokens.color;

const LANE: Record<RoadmapLane, { label: string; bg: string; fg: string }> = {
  "fast-track": { label: "FAST-TRACK", bg: c.teal, fg: "#fff" },
  roadmap: { label: "ROADMAP", bg: "#eef2f3", fg: "#4b5560" },
  charter: { label: "CHARTER", bg: c.warnBg2, fg: "#8a6d1f" },
};

const STAGES: { stage: RoadmapStage; header: string }[] = [
  { stage: "requested", header: "REQUESTED" },
  { stage: "order-desk", header: "AT ORDER DESK" },
  { stage: "laned", header: "IN A LANE" },
  { stage: "on-train", header: "ON THE TRAIN" },
  { stage: "delivered", header: "DELIVERED" },
];

const laneChip = (fg: string, bg: string) => ({
  display: "inline-flex",
  alignItems: "center",
  background: bg,
  color: fg,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  borderRadius: "4px",
  padding: "2px 7px",
});
const badgeChip = (fg: string, bg: string) => ({
  display: "inline-flex",
  alignItems: "center",
  background: bg,
  color: fg,
  fontSize: 10.5,
  fontWeight: 600,
  borderRadius: "4px",
  padding: "2px 7px",
});

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: 11.5, color: c.muted }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: color }} />
      {label}
    </Box>
  );
}

function RequestCard({ r, decisionDays }: { r: RoadmapRequest; decisionDays?: number }) {
  const derived = r.derived.length > 0;
  return (
    <Box
      sx={
        r.hero
          ? { background: "#f2faf9", border: `1.5px solid ${c.teal}`, borderRadius: "8px", p: "14px", boxShadow: brainTokens.shadow.heroCard }
          : { background: "#fff", border: `1px solid ${c.border}`, borderRadius: "8px", p: "13px 14px" }
      }
    >
      {r.hero && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontFamily: brainTokens.font.mono, fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", color: c.tealInk, background: "#d6efec", borderRadius: "4px", padding: "2px 7px", mb: "8px" }}>
          ✦ FLAGSHIP
        </Box>
      )}
      <Box sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, mb: "9px" }}>{r.title}</Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "9px" }}>
        {r.lane && <Box component="span" sx={laneChip(LANE[r.lane].fg, LANE[r.lane].bg)}>{LANE[r.lane].label}</Box>}
        {r.stage === "delivered" && r.fixVersion && (
          <Box component="span" sx={badgeChip(c.successText, c.successBg)}>Delivered · {r.fixVersion}</Box>
        )}
        {r.stage === "on-train" && r.fixVersion && (
          <Box component="span" sx={badgeChip(c.tealInk, c.tealTint)}>
            {r.fixVersion}{r.shipDate ? ` · ships ${r.shipDate}` : ""}
          </Box>
        )}
        {r.stage === "order-desk" && decisionDays != null && (
          <Box component="span" sx={badgeChip(decisionDays > 7 ? c.dangerText : "#5a626a", decisionDays > 7 ? c.dangerBg : "#eef2f3")}>
            Letter sent · {decisionDays}d ago
          </Box>
        )}
      </Box>
      {(r.votes ?? 0) > 1 && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 11, fontWeight: 600, color: "#4b5560", background: "#eef2f3", borderRadius: "999px", padding: "2px 9px", mb: "9px" }}>
          <Box component="span" sx={{ color: c.tealInk }}>▲</Box>
          {r.votes} customers want this
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <Box sx={{ fontSize: 11, color: c.faint2 }}>
          {r.stage === "order-desk" ? "awaiting decision" : `${r.daysInStage}d in stage`}
        </Box>
        {derived && (
          <Box
            title="Inferred — not yet from the new Jira fields"
            sx={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: 10.5, color: "#b0966a", borderBottom: "1px dashed #d8c39a", cursor: "help" }}
          >
            <Box component="span" sx={{ fontFamily: brainTokens.font.mono }}>~</Box>derived
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function CustomerRoadmapTabMui({ customerId }: { customerId: string }) {
  const [searchParams] = useSearchParams();
  const useMock = searchParams.get("mock") === "1";
  const { openAgent } = useAgent();
  const [view, setView] = useState<RoadmapView | null>(useMock ? mockRoadmapView : null);
  const [loading, setLoading] = useState(!useMock);

  useEffect(() => {
    if (useMock) {
      setView(mockRoadmapView);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    getRoadmap(customerId)
      .then((v) => live && setView(v))
      .catch(() => live && setView(null))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [customerId, useMock]);

  if (loading) return <Box sx={{ color: c.muted2, fontSize: 14, py: 4 }}>Loading roadmap…</Box>;
  if (!view) {
    return (
      <Box sx={{ background: "#fff", border: `1px solid ${c.border}`, borderRadius: "8px", p: "52px 22px", textAlign: "center" }}>
        <Box sx={{ fontSize: 16, fontWeight: 600, mb: "8px" }}>No roadmap yet</Box>
        <Box sx={{ fontSize: 13, color: c.muted2, maxWidth: 560, mx: "auto", lineHeight: 1.7 }}>
          No Jira org is mapped for this customer, or the roadmap adapter hasn't been deployed. Once it is, feature
          requests appear here on the Flightpath.
        </Box>
      </Box>
    );
  }

  const nudge = (d: { key: string; daysSinceLetter: number }) =>
    openAgent({
      kind: "explain",
      modelHint: "smart",
      prompt: `Draft a nudge for ${d.key} — ${d.daysSinceLetter} days since the Buy-or-Wait letter. Keep it warm and specific; this is a draft for a human to send.`,
    });

  const trainVersion = view.requests.find((r) => r.stage === "on-train")?.fixVersion ?? "4.16";
  const decisionByKey = new Map(view.decisionQueue.map((d) => [d.key, d]));
  const [lead, ...flags] = view.signals ?? [];

  const headline = [
    { label: "Total requests", value: view.counts.total, sub: "across all stages", color: c.ink },
    { label: "Awaiting Buy-or-Wait", value: view.counts.awaitingDecision, sub: view.counts.oldestAwaitingDays ? `oldest ${view.counts.oldestAwaitingDays}d` : "", color: c.dangerText },
    { label: "Fast-tracked", value: view.counts.fastTracked, sub: `on ${trainVersion}`, color: c.tealInk },
    { label: "On the roadmap", value: view.counts.onRoadmap, sub: "demand-driven", color: c.ink },
    { label: "Delivered · last 2 rel", value: view.counts.delivered, sub: "", color: c.successText },
  ];

  return (
    <Box>
      {/* headline counts */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "14px", mb: "20px" }}>
        {headline.map((k) => (
          <KpiCard key={k.label} label={k.label} value={k.value} valueColor={k.color} valueSize={26} hint={k.sub} />
        ))}
      </Box>

      {/* the flightpath */}
      <Box sx={{ background: "#fff", border: `1px solid ${c.border}`, borderRadius: "10px", p: "20px 22px", mb: "18px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "16px", flexWrap: "wrap", gap: "10px" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
            <Box sx={{ fontSize: 16, fontWeight: 700 }}>The Flightpath</Box>
            <Box sx={{ fontSize: 12.5, color: c.muted2 }}>{customerId} · release train {trainVersion}</Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
            <LegendDot color={c.teal} label="Fast-track" />
            <LegendDot color="#c7ced3" label="Roadmap" />
            <LegendDot color="#e9c765" label="Charter" />
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 11.5, color: c.muted }}>
              <Box component="span" sx={{ fontFamily: brainTokens.font.mono, color: "#b0966a" }}>~</Box>derived
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "14px", overflowX: "auto", pb: "6px" }}>
          {STAGES.map(({ stage, header }) => {
            const items = view.requests.filter((r) => r.stage === stage);
            const label = stage === "on-train" ? `${header} · ${trainVersion}` : header;
            return (
              <Box key={stage} sx={{ flex: 1, minWidth: 228, display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 11px" }}>
                  <MonoLabel color={c.muted2} letterSpacing="0.09em">{label}</MonoLabel>
                  <Box sx={{ fontSize: 11, color: "#b0b6bc" }}>{items.length}</Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", background: c.panelBg3, borderRadius: "8px", p: "10px", flex: 1 }}>
                  {items.map((r) => (
                    <RequestCard key={r.key} r={r} decisionDays={decisionByKey.get(r.key)?.daysSinceLetter} />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* demand signals + decision queue */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <Box sx={{ background: c.tealTint2, border: `1px solid ${c.tealTintBorder}`, borderRadius: "8px", p: "18px 20px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mb: "14px" }}>
            <Box sx={{ width: 24, height: 24, borderRadius: "7px", background: "#d9efec", color: c.teal, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
              <Sparkle size={13} />
            </Box>
            <MonoLabel color={c.teal}>AGENT · DEMAND SIGNALS</MonoLabel>
          </Box>
          {lead && <Box sx={{ fontSize: 13.5, color: "#1f3a3a", lineHeight: 1.6, mb: "16px" }}><AgentText text={lead.text} /></Box>}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "13px" }}>
            {flags.map((f, i) => (
              <Box key={i} sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <Box component="span" sx={{ color: c.teal, flex: "none", mt: "1px", fontSize: 11 }}>◆</Box>
                <Box sx={{ fontSize: 12.5, color: "#2c4a4a", lineHeight: 1.55 }}><AgentText text={f.text} /></Box>
              </Box>
            ))}
            {flags.length === 0 && !lead && <Box sx={{ fontSize: 12.5, color: c.muted2 }}>No demand signals yet.</Box>}
          </Box>
        </Box>

        <Box sx={{ background: "#fff", border: `1px solid ${c.border}`, borderRadius: "8px", p: "18px 20px" }}>
          <Box sx={{ fontSize: 15, fontWeight: 600, mb: "5px" }}>Decision queue</Box>
          <Box sx={{ fontSize: 12.5, color: c.muted2, mb: "12px" }}>Awaiting the customer's Buy-or-Wait response — our follow-ups.</Box>
          {view.decisionQueue.length === 0 ? (
            <Box sx={{ fontSize: 13, color: c.muted2, py: 2 }}>Nothing awaiting a decision.</Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {view.decisionQueue.map((d) => {
                const stale = d.daysSinceLetter > 7;
                return (
                  <Box key={d.key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", py: "13px", borderTop: `1px solid ${c.borderLight2}` }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ fontSize: 13.5, fontWeight: 600, mb: "4px" }}>{d.title}</Box>
                      <Box sx={{ fontSize: 12, color: c.faint }}>Offered: {d.lanesOffered.join("  ·  or  ·  ")}</Box>
                    </Box>
                    <Box sx={{ textAlign: "right", flex: "none" }}>
                      <Box sx={{ mb: "7px", fontSize: 12, color: stale ? c.dangerText : c.muted2, fontWeight: stale ? 700 : 600 }}>
                        {d.daysSinceLetter} days since letter
                      </Box>
                      <Box component="button" onClick={() => nudge(d)} sx={{ fontSize: 11.5, fontWeight: 500, color: c.ink3, background: "#fff", border: "1px solid #d7dbdf", borderRadius: "6px", padding: "5px 13px", cursor: "pointer", fontFamily: brainTokens.font.sans, "&:hover": { background: "#f6f8f9" } }}>
                        Nudge
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
